import React, { useEffect, useState } from "react";
import { Box, Button, Modal } from "@mui/material";
import { Typography } from "@material-ui/core";
import DatabaseEntities from "../../model/enum/DatabaseEntities";
import { DarkGrey, PrimaryColor } from "../../shared/styles/colors";
import { Spinner } from "../../components";
import { useSearchParams } from "react-router-dom";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import RoleModal from "./RoleModal";
import Role from "../../model/role";
import Permission from "../../model/permission";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ApiService from "../../service/ApiService";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import RolePermission from "../../model/rolePermission";
import Misc from "../../utils/Misc";
import useStyles from "./styles";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "35rem",
  bgcolor: "background.paper",
  order: "none",
  borderRadius: "8px",
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
  maxHeight: "90vh", // Restrict maximum height to 80% of the viewport height
  overflowY: "auto", // Enable vertical scrolling
  "@media (max-width: 640px)": {
    width: "80%",
  },
};

interface Props {
  role: Role;
  permissions: Permission[];
  resetList: () => void;
  onOpen?: (id?: string) => void;
  onClose?: () => void;
}

const RoleDetailModal = ({
  role,
  permissions,
  resetList,
  onOpen,
  onClose,
}: Props) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [togglePermissions, setTogglePermissions] = useState(true);
  const [rolePermissions, setRolePermissions] = useState<RolePermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  const handleOpen = () => {
    setOpen(true);
    if (onOpen) {
      onOpen(role.id?.toString());
    }
  };

  const handleClose = () => {
    setOpen(false);
    if (onClose) {
      onClose();
    }
  };

  useEffect(() => {
    const roleId = searchParams.get("roleId");
    if (!open && roleId && roleId === role.id?.toString()) {
      handleOpen();
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await ApiService.get<RolePermission[]>(
        `${DatabaseEntities.ROLES}/${role.id}/${DatabaseEntities.PERMISSIONS}`
      )
        .then((res) => {
          setRolePermissions(res);
        })
        .finally(() => {
          setLoading(false);
        });
    };
    if (open && Misc.isArrayEmpty(rolePermissions)) fetchData();
  }, [open]);

  const handleTogglePermissions = () =>
    setTogglePermissions((togglePermissions) => !togglePermissions);

  const isPermissionActive = (permissionId: number) => {
    if (loading || Misc.isArrayEmpty(rolePermissions)) return false;

    return rolePermissions.some((rp) => rp.permissionId === permissionId);
  };

  return (
    <div>
      <Button onClick={handleOpen}>
        <MoreVertIcon sx={{ color: PrimaryColor}}/>
      </Button>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={{ overflowY: "auto" }}
      >
        {loading ? (
          <Spinner />
        ) : (
          <Box sx={style}>
            <div className={classes.headerDiv}>
              <Typography className={classes.title}>Role info</Typography>
              <div className={classes.headerSubDiv}>
                <RoleModal
                  role={role}
                  permissions={permissions}
                  rolePermissions={rolePermissions}
                  resetList={resetList}
                />
                <Button onClick={handleClose} sx={{ color: "black" }}>
                  <CloseIcon />
                </Button>
              </div>
            </div>
            <hr />
            <div className={classes.form}>
              <Typography>Name</Typography>
              <Typography className={classes.inputDetail}>
                {role.name}
              </Typography>
              <div className={classes.permissionDiv}>
                <Typography className={classes.subTitle}>
                  Permissions
                </Typography>
                {togglePermissions ? (
                  <ExpandLessIcon onClick={handleTogglePermissions} />
                ) : (
                  <ExpandMoreIcon onClick={handleTogglePermissions} />
                )}
              </div>
              {togglePermissions && (
                <>
                  <hr />
                  <div className={classes.permissionsList}>
                    {permissions.map((perm: Permission) => (
                      <div className={classes.permissionItem} key={perm.id}>
                        <Typography>{perm.name}</Typography>
                        {isPermissionActive(perm.id) ? (
                          <Button
                            sx={{
                              color: PrimaryColor,
                              "&:disabled": {
                                color: PrimaryColor,
                              },
                            }}
                            disabled
                          >
                            <ToggleOnIcon />
                          </Button>
                        ) : (
                          <Button sx={{ color: DarkGrey }} disabled>
                            <ToggleOffIcon />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </Box>
        )}
      </Modal>
    </div>
  );
};

export default RoleDetailModal;
